import { IntlShape } from 'react-intl';
import { Berth } from '../../interfaces/PortData';
import { Mooring } from '../../interfaces/VesselScenario';
import { valueToDisplay } from '../conversions';

export type MarkerType = 'bow' | 'stern' | 'center';

export interface BerthPocketValidationResult {
  valid: boolean;
  warning?: {
    vesselCenter: number;
    berthStart: number;
    berthEnd: number;
    berthName: string;
    exceedsStart: boolean;
    exceedsEnd: boolean;
  };
}

/**
 * Validates if the vessel's center (midships) is positioned within the assigned berth pocket boundaries.
 *
 * This validation checks if the center point falls within the berth pocket defined by:
 * - Start: berthStartFromTerminal
 * - End: berthStartFromTerminal + berthPocketLength
 *
 * @param berth The berth configuration
 * @param mooring The mooring configuration containing vessel position
 * @returns Validation result with warning details if vessel is outside berth pocket
 */
export function validateBerthPocketPosition(
  berth: Berth,
  mooring: Mooring,
): BerthPocketValidationResult {
  if (!berth.berthPocketLength || berth.berthPocketLength <= 0) {
    return { valid: true };
  }

  // Vessel center position along the berth (midships)
  const vesselCenter = (mooring.bowMarker + mooring.sternMarker) / 2;

  if (vesselCenter === null || vesselCenter === undefined || isNaN(vesselCenter)) {
    return { valid: true };
  }

  // Calculate berth pocket boundaries
  const berthStart = berth.berthStartFromTerminal;
  const berthEnd = berth.berthStartFromTerminal + berth.berthPocketLength;

  // Check if vessel center is within berth pocket
  const isWithinPocket = vesselCenter >= berthStart && vesselCenter <= berthEnd;

  if (isWithinPocket) {
    return { valid: true };
  }

  return {
    valid: false,
    warning: {
      vesselCenter,
      berthStart,
      berthEnd,
      berthName: berth.berthName,
      exceedsStart: vesselCenter < berthStart,
      exceedsEnd: vesselCenter > berthEnd,
    },
  };
}

/**
 * Formats the berth pocket validation warning for display to the user
 *
 * @param validationResult The validation result from validateBerthPocketPosition
 * @param isSiUnits Whether to display in SI units (meters) or imperial (feet)
 * @param intl The react-intl IntlShape for localization
 * @param type The marker type being validated (bow, stern, or center)
 * @returns Formatted warning message details
 */
export function formatBerthPocketWarning(
  validationResult: BerthPocketValidationResult,
  isSiUnits: boolean = true,
  intl?: IntlShape,
  type: MarkerType = 'center',
): { title: string; description: string } | null {
  if (validationResult.valid || !validationResult.warning) {
    return null;
  }

  const { vesselCenter, berthStart, berthEnd, berthName, exceedsStart, exceedsEnd } = validationResult.warning;

  const pocketRange = `${valueToDisplay(berthStart, 'm', isSiUnits, true, 1)} - ${valueToDisplay(berthEnd, 'm', isSiUnits, true, 1)}`;
  const markerPosition = valueToDisplay(vesselCenter, 'm', isSiUnits, true, 1);

  // If intl is not provided, fall back to non-localized strings
  if (!intl) {
    const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
    const direction = exceedsStart ? 'before' : exceedsEnd ? 'beyond' : 'outside';
    return {
      title: `${typeLabel} positioned ${direction} berth pocket`,
      description: `${typeLabel} is at ${markerPosition}, but ${berthName} berth pocket extends from ${pocketRange}`,
    };
  }

  const typeLabel = intl.formatMessage({
    id: `components.ma.forms.berthPocket.type.${type}`,
    defaultMessage: type.charAt(0).toUpperCase() + type.slice(1),
  });

  const titleMessageId = exceedsStart
    ? 'components.ma.forms.berthPocket.positionedBefore'
    : exceedsEnd
      ? 'components.ma.forms.berthPocket.positionedBeyond'
      : 'components.ma.forms.berthPocket.positionedOutside';

  const title = intl.formatMessage(
    { id: titleMessageId, defaultMessage: titleMessageId },
    { markerType: typeLabel },
  );

  const description = intl.formatMessage(
    {
      id: 'components.ma.forms.berthPocket.description',
      defaultMessage: '{markerType} is at {position}, but {berthName} berth pocket extends from {pocketRange}',
    },
    { markerType: typeLabel, position: markerPosition, berthName, pocketRange },
  );

  return { title, description };
}
